import { Calendar, Link, Paperclip, Files } from "lucide-react";
import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Modal,
  Container,
  Group,
  Checkbox,
  createStyles,
  InputWrapper,
  Badge,
} from "@mantine/core";
import dayjs from "dayjs";
import moment from "moment";
import { DatePicker } from "@mantine/dates";

import { AxiosError } from "axios";
import { HttpClientError } from "../../../providers/HttpClient";
import ReconEventInstance from "../services/model/ReconEventInstance";
import useUpdateReconEventInstanceApiService from "../services/api/UpdateReconEventInstanceApiService";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { reconciledODPBadge, xeroInvoice } from "../../../utils/Theme";
import useGetReconFileApiService, {
  useGetReconFilesListApiService,
} from "../services/api/GetReconFileApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import MultipleReconFilesModal from "./MultipleReconFilesModal";

const useStyles = createStyles((theme) => ({
  requestNowBtn: {
    position: "absolute",
    bottom: "0",
    left: "0",
    padding: "0 10px 10px 10px",
    boxSizing: "border-box",
    width: "100%",

    [`@media (max-width: ${theme.breakpoints.xs}px)`]: {
      padding: "0",
      position: "initial",
    },
  },
  reconInvoices: {
    position: "absolute",
    left: "0",
    padding: "24px",
    maxHeight: "40vh",
    overflowY: "scroll",
    width: "100%",
  },
  reconAttachments: {
    backgroundColor: "white",
    padding: "24px",
  },
}));

type ReconInstanceDetailsProps = {
  reconInstance: ReconEventInstance | null;
  onSuccess?: () => void;
};

const ReconInstanceDetails: React.FC<ReconInstanceDetailsProps> = ({
  reconInstance,
  onSuccess,
}) => {
  const { showToast } = useToasts();
  const today = new Date();

  const getInvoices = (): any[] => {
    const invoicesData: any = [];

    if (reconInstance?.extendedProperties?.private) {
      const invoiceKeys = Object.keys(
        reconInstance?.extendedProperties?.private
      ).filter((objKey) => objKey.includes("invoice_"));

      invoiceKeys.forEach((invoiceKey) => {
        const invoice =
          reconInstance?.extendedProperties?.private[invoiceKey].split("]:[");

        invoicesData.push({
          url: invoice[0],
          contact: invoice[1],
        });
      });
    }

    return invoicesData;
  };
  const xeroInvoices = getInvoices();

  const isReconciled =
    reconInstance?.extendedProperties?.private.status === "RECONCILED";

  const recurrence = reconInstance?.extendedProperties?.private.recurrence;

  const frequencyType = recurrence?.split("FREQ=")[1].split(";")[0];

  const interval = Number(recurrence?.split("INTERVAL=")[1].split(";")[0]);

  const initialDate = reconInstance?.originalStartTime?.date;

  const getMinReconDate = (): Date => {
    if (frequencyType === "WEEKLY" && interval === 1) {
      const minDate = dayjs(initialDate).subtract(3, "day").toDate();

      if (today < minDate) {
        return minDate;
      }
    }

    if (frequencyType === "WEEKLY" && interval === 2) {
      const minDate = dayjs(initialDate).subtract(6, "day").toDate();

      if (today < minDate) {
        return minDate;
      }
    }

    if (frequencyType === "MONTHLY") {
      const minDate = dayjs(initialDate).subtract(14, "day").toDate();

      if (today < minDate) {
        return minDate;
      }
    }

    return today;
  };

  const getMaxReconDate = (): Date => {
    if (frequencyType === "WEEKLY" && interval === 1) {
      return dayjs(initialDate).add(3, "day").toDate();
    }

    if (frequencyType === "WEEKLY" && interval === 2) {
      return dayjs(initialDate).add(6, "day").toDate();
    }

    if (frequencyType === "MONTHLY") {
      return dayjs(initialDate).add(14, "day").toDate();
    }

    return today;
  };

  const canRequestImmediateRecon = (): boolean => {
    if (reconInstance) {
      const startDate = reconInstance?.start.date;

      if (
        moment(startDate).format("YYYY-MM-DD") ===
        moment(today).format("YYYY-MM-DD")
      ) {
        return false;
      }
    }

    return true;
  };

  const getExcludedDates = (date: Date): boolean => {
    const day = moment(date).day();

    if (day === 0 || day === 6) {
      return true;
    }

    return false;
  };

  const canUpdate = (): boolean => {
    if (isReconciled) {
      return false;
    }

    let reconDate: Date;

    if (reconInstance) {
      reconDate = new Date(reconInstance?.start.date);

      if (reconDate.getTime() < today.getTime()) {
        return false;
      }

      if (
        moment(reconDate).format("YYYY-MM-DD") ===
        moment(today).format("YYYY-MM-DD")
      ) {
        return false;
      }
    } else {
      return false;
    }
    return true;
  };

  const hasFeatureEnabled = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYER_PORTAL_AUTOMATED_ODP_INVOICING_UPDATE_DATES
  );
  const originalDate = new Date(reconInstance ? reconInstance.start.date : "");
  const [value, setValue] = useState<Date | null>(
    new Date(reconInstance ? reconInstance.start.date : "")
  );

  const [isConfirmReconModalOpen, setIsConfirmReconModalOpen] = useState(false);
  const [understandsEffects, setUnderstandsEffects] = useState(false);

  const [
    isImmediateReconRequestConfirmed,
    setIsImmediateReconRequestConfirmed,
  ] = useState(false);

  const [isMultipleFilesModalOpen, setIsMultipleFilesModalOpen] =
    useState(false);

  const {
    isSuccess: isUpdateSuccess,
    isError: isUpdateFailure,
    error: updateError,
    isLoading: isUpdateLoading,
    mutate: onUpdate,
  } = useUpdateReconEventInstanceApiService();

  const {
    isSuccess: isDownloadFileSuccess,
    isError: isDownloadFileFailure,
    error: downloadFileError,
    isLoading: isDownloadFileLoading,
    mutate: downloadReconFile,
    data: reconFileResponse,
  } = useGetReconFileApiService();

  // Fetch multiple recon files when modal is opened
  const {
    data: multipleFilesData,
    isLoading: isLoadingMultipleFiles,
    error: multipleFilesError,
  } = useGetReconFilesListApiService({
    employerId: reconInstance?.extendedProperties?.private.employerId,
    date: reconInstance?.start.date,
    enabled: isMultipleFilesModalOpen && !!reconInstance,
  });

  const failureNotification = (err: AxiosError | null): void =>
    showToast({
      strapiId: "recon_date_update_failed",
      data: { message: HttpClientError(err) },
    });

  const updateInstanceDate = (newDate: Date | null): void => {
    if (reconInstance === null) return;

    const updatedReconInstance = {
      ...reconInstance,
      start: {
        date: moment(newDate).format("YYYY-MM-DD"),
        timeZone: reconInstance.start.timeZone,
      },
      end: {
        date: moment(newDate).format("YYYY-MM-DD"),
        timeZone: reconInstance.end.timeZone,
      },
    };
    onUpdate({
      eventInstance: updatedReconInstance,
      newDate: moment(newDate).format("YYYY-MM-DD"),
      oldDate: moment(originalDate).format("YYYY-MM-DD"),
    });
  };

  const viewInvoice = (url: string): void => {
    window.open(url);
  };

  useEffect(() => {
    if (isImmediateReconRequestConfirmed) {
      updateInstanceDate(new Date());
    }
  }, [isImmediateReconRequestConfirmed]);

  useEffect(() => {
    if (isDownloadFileSuccess && reconFileResponse) {
      const reconFile = reconFileResponse.url;
      window.open(reconFile, "_blank")?.focus();
      showToast({
        strapiId: "recon_file_download_success",
      });
    }
  }, [reconFileResponse, isDownloadFileSuccess]);

  useEffect(() => {
    if (isDownloadFileFailure || downloadFileError) {
      showToast({
        strapiId: "recon_file_download_failed",
        data: {
          message: HttpClientError(
            downloadFileError ?? new Error("Unknown error")
          ),
        },
      });
    }
  }, [isDownloadFileFailure, downloadFileError]);

  useEffect(() => {
    if (isUpdateSuccess && onSuccess) {
      showToast({ strapiId: "recon_date_update_success" });
      onSuccess();
    }

    if (isUpdateFailure) {
      failureNotification(updateError);
    }
  }, [isUpdateSuccess, isUpdateFailure]);

  const { classes } = useStyles();

  return (
    <div>
      <Modal
        size="50%"
        title="Request immediate Recon"
        opened={isConfirmReconModalOpen}
        closeOnClickOutside={false}
        centered
        onClose={() => setIsConfirmReconModalOpen(false)}
      >
        <Container>
          <Space h="xl" />
          By requesting that a recon be sent immediately, you are confirming
          that your current recon date will be moved to today and a recon will
          be issued right away.
          <Space h="xl" />
          <Checkbox
            checked={understandsEffects}
            label="I understand and confirm that I want my recon sent immediately."
            onChange={(c) => setUnderstandsEffects(c.target.checked)}
            transitionDuration={0}
          />
          <Space h="xl" />
          <Group spacing="xl" position="apart">
            <Button
              onClick={() => setIsConfirmReconModalOpen(false)}
              variant="default"
            >
              Cancel
            </Button>
            <Button
              disabled={!understandsEffects}
              size="sm"
              loading={isUpdateLoading}
              onClick={() => setIsImmediateReconRequestConfirmed(true)}
            >
              Confirm
            </Button>
          </Group>
          <Space h="xl" />
        </Container>
      </Modal>
      <h3> {reconInstance && reconInstance.summary}</h3>
      <Space h="xl" />
      <DatePicker
        minDate={getMinReconDate()}
        maxDate={getMaxReconDate()}
        disabled={!canUpdate()}
        excludeDate={
          getExcludedDates
          // if (
          //   moment(originalDate).format("YYYY-MM-DD") ===
          //   moment(date).format("YYYY-MM-DD")
          // )
          //   return false;
          // if (
          //   moment(originalDate).format("YYYY-MM-DD") ===
          //   moment().format("YYYY-MM-DD")
          // )
          //   return true;

          // const day = moment(date).day();
          // return day === 0 || day === 6;
        }
        onChange={(d) => setValue(d)}
        value={value}
        label="Event date"
        icon={<Calendar size={16} />}
      />
      <Space h="xl" />
      {hasFeatureEnabled &&
        reconInstance?.extendedProperties?.private.status !== "RECONCILED" && (
          <Button
            disabled={!canUpdate()}
            id="updateReconInstanceDate"
            name="updateReconInstanceDate"
            fullWidth
            radius="md"
            type="submit"
            loaderPosition="right"
            color="jem-theme"
            loading={isUpdateLoading}
            onClick={() =>
              moment(value).format("YYYY-MM-DD") ===
              moment(new Date()).format("YYYY-MM-DD")
                ? setIsConfirmReconModalOpen(true)
                : updateInstanceDate(value)
            }
          >
            Update Date
          </Button>
        )}

      {reconInstance && isReconciled && (
        <Badge
          style={{
            backgroundColor: reconciledODPBadge.defaultCompanyLimit,
          }}
        >
          Reconciled
        </Badge>
      )}

      <Space h="xl" />

      {isReconciled && xeroInvoices.length > 0 && (
        <InputWrapper
          id="input-demo"
          label="Recon Invoices"
          description="Invoices Created for the recon"
        >
          <div className={classes.reconInvoices}>
            {xeroInvoices.map((inv: any) => (
              <>
                <Button
                  style={{ padding: 0, color: xeroInvoice.link }}
                  onClick={() => viewInvoice(inv.url)}
                  leftIcon={<Link size={12} />}
                  variant="white"
                >
                  {inv.contact}
                </Button>
                <Space />
              </>
            ))}
          </div>
        </InputWrapper>
      )}

      <Space h="xl" />

      {isReconciled && xeroInvoices.length > 0 && (
        <div className={classes.reconAttachments}>
          <InputWrapper
            style={{ bottom: "0" }}
            id="input-demo"
            label="Recon Attachments"
            description="Breakdown of amounts advanced and fees charged"
          >
            <Group spacing="md" align="flex-start">
              {/* Primary file download button */}
              <Button
                style={{ padding: 0 }}
                onClick={() =>
                  downloadReconFile({
                    employerId:
                      reconInstance.extendedProperties?.private.employerId,
                    reconFile:
                      reconInstance.extendedProperties?.private.reconFile,
                  })
                }
                leftIcon={<Paperclip size={12} />}
                variant="white"
                loading={isDownloadFileLoading}
              >
                {reconInstance.extendedProperties?.private.reconFile ||
                  "Download Recon File"}
              </Button>

              {/* See More Files button */}
              <Button
                size="sm"
                variant="outline"
                color="jem-theme"
                leftIcon={<Files size={14} />}
                onClick={() => setIsMultipleFilesModalOpen(true)}
              >
                See More Files
              </Button>
            </Group>
          </InputWrapper>
        </div>
      )}

      {isReconciled && xeroInvoices.length === 0 && (
        <div>No advances taken in the recon period.</div>
      )}

      <Space h="xl" />

      {hasFeatureEnabled && !isReconciled && (
        <>
          <Space h="xl" />
          <div className={classes.requestNowBtn}>
            <Button
              disabled={!canRequestImmediateRecon()}
              fullWidth
              radius="md"
              type="submit"
              loaderPosition="right"
              color="jem-theme"
              loading={isUpdateLoading}
              onClick={() => setIsConfirmReconModalOpen(true)}
            >
              Request a Recon Now
            </Button>
          </div>
        </>
      )}

      {/* Multiple Recon Files Modal */}
      <MultipleReconFilesModal
        opened={isMultipleFilesModalOpen}
        onClose={() => setIsMultipleFilesModalOpen(false)}
        filesData={multipleFilesData}
        employerId={reconInstance?.extendedProperties?.private.employerId || ""}
        isLoading={isLoadingMultipleFiles}
        error={multipleFilesError}
      />
    </div>
  );
};

export default ReconInstanceDetails;
